import React, { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, FileSpreadsheet, FileText, Plus, Trash2, ExternalLink, Sparkles, Loader2, TrendingUp, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ReportTimeline from "@/components/ReportTimeline";

const COLS = ["No", "Nama", "WhatsApp", "Email", "Video/Post", "Date Post", "Tier", "Followers", "Reach", "Views", "Like", "Comment", "Share", "Save", "Engagement", "ER (%)"];
const NUM = ["followers", "reach", "views", "likes", "comment", "share", "save"];
const waHref = (num) => {
  const d = String(num ?? "").replace(/\D/g, "");
  if (!d) return undefined;
  return `https://wa.me/${d.startsWith("0") ? "62" + d.slice(1) : d}`;
};

export default function ReportPanel() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/reports").then((r) => setRows(r.data)).catch(() => {}), []);
  const loadAnalysis = useCallback(() => api.get("/reports/analysis").then((r) => setAnalysis(r.data && r.data.insights && r.data.row_count > 0 ? r.data : null)).catch(() => {}), []);
  useEffect(() => { load(); loadAnalysis(); }, [load, loadAnalysis]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post("/reports/analyze");
      setAnalysis(res.data);
      toast.success("Analisa AI selesai");
    } catch (e) {
      toast.error("Gagal membuat analisa");
    } finally {
      setAnalyzing(false);
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      setAnalyzing(true);
      const res = await api.post("/reports/import", fd);
      toast.success(`${res.data.imported} baris diimport — membuat analisa AI…`);
      if (res.data.analysis) setAnalysis(res.data.analysis);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal import Excel");
    } finally {
      setBusy(false);
      setAnalyzing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (format) => {
    try {
      const res = await api.get(`/reports/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "pdf" ? "showtynion-kol-report.pdf" : "showtynion-kol-report.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Gagal export");
    }
  };

  const removeRow = async (id) => {
    try {
      await api.delete(`/reports/${id}`);
      load();
    } catch (e) {
      toast.error("Gagal menghapus baris");
    }
  };

  return (
    <div data-testid="report-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl text-showty-navy">KOL Report</h3>
          <p className="text-sm text-muted-foreground">Import data dari Excel, lalu unduh sebagai Excel atau PDF.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onImport} className="hidden" data-testid="report-import-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" className="rounded-full" data-testid="report-import-btn">
            <Upload className="w-4 h-4 mr-1.5" /> {busy ? "Mengimport…" : "Import Excel"}
          </Button>
          <Button onClick={() => download("excel")} className="rounded-full bg-showty-navy hover:bg-showty-navy/90 text-white" data-testid="report-export-excel">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel
          </Button>
          <Button onClick={() => download("pdf")} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="report-export-pdf">
            <FileText className="w-4 h-4 mr-1.5" /> PDF
          </Button>
          <Button onClick={runAnalysis} disabled={analyzing} className="rounded-full bg-showty-gold hover:opacity-90 text-white" style={{ backgroundColor: "#7284c4" }} data-testid="report-analyze-btn">
            {analyzing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />} Analisa AI
          </Button>
          <AddRowDialog onDone={load} />
        </div>
      </div>

      <Tabs defaultValue="data">
        <TabsList className="bg-showty-secondary mb-4">
          <TabsTrigger value="data" data-testid="report-tab-data">Data & Analisa</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="report-tab-timeline">Timeline & Kalender</TabsTrigger>
        </TabsList>

        <TabsContent value="data">

      {(analyzing || analysis) && (
        <div className="mb-5 rounded-2xl border border-showty-navy/15 bg-white overflow-hidden" data-testid="report-analysis-card">
          <div className="flex items-center gap-2 px-5 py-3 bg-showty-navy text-white">
            <Sparkles className="w-4 h-4" />
            <span className="font-display text-lg">Analisa AI Otomatis</span>
            {analysis?.generated_at && <span className="text-xs text-white/60 ml-auto">{new Date(analysis.generated_at).toLocaleString("id-ID")}</span>}
          </div>
          <div className="p-5">
            {analyzing && !analysis && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Menganalisa data yang diupload…</div>
            )}
            {analysis && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <Stat label="Total KOL" value={analysis.stats?.row_count ?? 0} />
                  <Stat label="Rata-rata ER" value={`${analysis.stats?.avg_er ?? 0}%`} />
                  <Stat label="Total Reach" value={(analysis.stats?.total_reach ?? 0).toLocaleString("id-ID")} />
                  <Stat label="Total Engagement" value={(analysis.stats?.total_engagement ?? 0).toLocaleString("id-ID")} />
                </div>
                {analysis.stats?.tiers && Object.keys(analysis.stats.tiers).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {Object.entries(analysis.stats.tiers).map(([t, c]) => (
                      <span key={t} className="text-xs rounded-full bg-showty-cream border border-border px-3 py-1 text-showty-navy">{t}: <b>{c}</b></span>
                    ))}
                  </div>
                )}
                {analysis.stats?.top_er?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-wide text-showty-red font-semibold flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Top Engagement Rate</p>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      {analysis.stats.top_er.map((t, i) => (
                        <span key={i} className="text-xs rounded-lg bg-showty-pink/40 border border-border px-3 py-1.5 text-showty-navy">{t.name} · <b>{t.er}%</b>{t.tier ? ` · ${t.tier}` : ""}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="rounded-xl bg-showty-cream border border-border p-4">
                  <p className="text-sm text-showty-navy whitespace-pre-wrap leading-relaxed" data-testid="analysis-insights">{analysis.insights}</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="report-table">
            <thead>
              <tr className="bg-showty-navy text-white text-left">
                {COLS.map((c) => <th key={c} className="px-3 py-2.5 whitespace-nowrap font-medium text-xs">{c}</th>)}
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={COLS.length + 1} className="px-4 py-12 text-center text-muted-foreground">Belum ada data. Import Excel atau tambah manual.</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={r.id} className="border-t border-border even:bg-showty-cream/50" data-testid={`report-row-${r.id}`}>
                  <td className="px-3 py-2">{i + 1}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-medium text-showty-navy">{r.name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.phone ? <a href={waHref(r.phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-green-600 text-white px-2.5 py-1 text-xs font-semibold hover:bg-green-700" data-testid={`report-wa-${r.id}`}><Phone className="w-3 h-3" /> WA</a> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.email}</td>
                  <td className="px-3 py-2">{r.link_post ? <a href={r.link_post} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-showty-red text-white px-2.5 py-1 text-xs font-semibold hover:bg-[#9e1f24]" data-testid={`report-video-${r.id}`}><ExternalLink className="w-3 h-3" /> Video</a> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.date_post}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.tier}</td>
                  <td className="px-3 py-2">{r.followers?.toLocaleString?.() ?? r.followers}</td>
                  <td className="px-3 py-2">{r.reach?.toLocaleString?.() ?? r.reach}</td>
                  <td className="px-3 py-2">{r.views?.toLocaleString?.() ?? r.views}</td>
                  <td className="px-3 py-2">{r.likes}</td>
                  <td className="px-3 py-2">{r.comment}</td>
                  <td className="px-3 py-2">{r.share}</td>
                  <td className="px-3 py-2">{r.save}</td>
                  <td className="px-3 py-2 font-medium">{r.engagement}</td>
                  <td className="px-3 py-2 font-semibold text-showty-red">{r.er}%</td>
                  <td className="px-3 py-2"><button onClick={() => removeRow(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`report-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Kolom Excel yang dikenali: Nama, Nomor Telfon (→ tombol WA), Email, Link Post (→ tombol Video), Date Post, Tier, Followers, Reach, Views, Like, Comment, Share, Save, Engagement, ER. Engagement & ER dihitung otomatis bila kosong.</p>
        </TabsContent>

        <TabsContent value="timeline">
          <ReportTimeline />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-border bg-showty-cream/60 px-4 py-3">
    <p className="font-display text-2xl text-showty-navy">{value}</p>
    <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
  </div>
);

const AddRowDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const empty = { name: "", phone: "", email: "", link_post: "", date_post: "", tier: "", followers: "", reach: "", views: "", likes: "", comment: "", share: "", save: "" };
  const [f, setF] = useState(empty);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name) return toast.error("Nama wajib diisi");
    const payload = { ...f };
    NUM.forEach((k) => { payload[k] = Number(f[k]) || 0; });
    await api.post("/reports", payload);
    toast.success("Data ditambahkan");
    setOpen(false);
    setF(empty);
    onDone?.();
  };
  const fields = [
    ["name", "Nama"], ["phone", "Nomor Telfon"], ["email", "Email"], ["link_post", "Link Post"], ["date_post", "Date Post"], ["tier", "Tier"],
    ["followers", "Followers"], ["reach", "Reach"], ["views", "Views"], ["likes", "Like"], ["comment", "Comment"], ["share", "Share"], ["save", "Save"],
  ];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="report-add-btn"><Plus className="w-4 h-4 mr-1" /> Manual</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="report-add-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-showty-navy">Tambah Data Report</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-1">
          {fields.map(([k, label]) => (
            <div key={k} className={k === "name" || k === "link_post" ? "col-span-2" : ""}>
              <Label className="text-xs">{label}</Label>
              <Input type={NUM.includes(k) ? "number" : "text"} className="mt-1" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`report-field-${k}`} />
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="report-add-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
